import React, { useEffect, useMemo } from 'react';
import {
  MapContainer,
  TileLayer,
  CircleMarker,
  Tooltip,
  useMap,
} from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

import { BRAZIL_STATE_CENTROIDS } from './brazilStates';
import { MapWrap } from './styles';

export interface MapCityEntry {
  city_name: string;
  state: string;
  ibge_code: number;
  total: number;
}

export type MapMode = 'complaints' | 'workers';

interface Props {
  data: MapCityEntry[];
  mode: MapMode;
  itemLabel?: string;
}

interface AggregatedState {
  uf: string;
  name: string;
  lat: number;
  lng: number;
  total: number;
  cities: number;
  topCities: { city: string; total: number }[];
}

const MODE_COLORS: Record<MapMode, { fill: string; stroke: string }> = {
  complaints: { fill: '#f38725', stroke: '#c76a13' },
  workers: { fill: '#006633', stroke: '#00451f' },
};

// Centro geográfico aproximado do Brasil
const BRAZIL_CENTER: [number, number] = [-14.235, -51.925];
const BRAZIL_ZOOM = 4;

/** Força leaflet a recalcular o tamanho depois que o card anima/monta. */
const InvalidateOnMount: React.FC = () => {
  const map = useMap();
  useEffect(() => {
    const id1 = window.setTimeout(() => map.invalidateSize(), 0);
    const id2 = window.setTimeout(() => map.invalidateSize(), 250);
    return () => {
      window.clearTimeout(id1);
      window.clearTimeout(id2);
    };
  }, [map]);
  return null;
};

const BrazilMap: React.FC<Props> = ({ data, mode, itemLabel }) => {
  const aggregated = useMemo<AggregatedState[]>(() => {
    const map = new Map<string, AggregatedState>();
    data.forEach(entry => {
      const centroid = BRAZIL_STATE_CENTROIDS[entry.state];
      if (!centroid) return;
      const existing = map.get(entry.state);
      if (existing) {
        existing.total += entry.total;
        existing.cities += 1;
        existing.topCities.push({ city: entry.city_name, total: entry.total });
      } else {
        map.set(entry.state, {
          uf: entry.state,
          name: centroid.name,
          lat: centroid.lat,
          lng: centroid.lng,
          total: entry.total,
          cities: 1,
          topCities: [{ city: entry.city_name, total: entry.total }],
        });
      }
    });

    return Array.from(map.values()).map(s => ({
      ...s,
      topCities: s.topCities.sort((a, b) => b.total - a.total).slice(0, 3),
    }));
  }, [data]);

  const maxTotal = useMemo(
    () => aggregated.reduce((acc, s) => Math.max(acc, s.total), 0),
    [aggregated],
  );

  const label =
    itemLabel || (mode === 'complaints' ? 'denúncias' : 'trabalhadores');
  const colors = MODE_COLORS[mode];

  const getRadius = (total: number) => {
    if (maxTotal === 0) return 8;
    const min = 8;
    const max = 28;
    return min + (total / maxTotal) * (max - min);
  };

  return (
    <MapWrap>
      <MapContainer
        center={BRAZIL_CENTER}
        zoom={BRAZIL_ZOOM}
        minZoom={3}
        maxZoom={8}
        scrollWheelZoom={false}
        zoomControl={false}
        attributionControl={false}
        style={{ width: '100%', height: '100%' }}
      >
        <InvalidateOnMount />
        <TileLayer
          url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          crossOrigin
        />

        {aggregated.map(state => (
          <CircleMarker
            key={state.uf}
            center={[state.lat, state.lng]}
            radius={getRadius(state.total)}
            pathOptions={{
              color: colors.stroke,
              fillColor: colors.fill,
              fillOpacity: 0.7,
              weight: 2,
            }}
          >
            <Tooltip direction="top" offset={[0, -6]} opacity={1}>
              <div style={{ lineHeight: 1.4 }}>
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: 12,
                    marginBottom: 2,
                  }}
                >
                  {`${state.name} · ${state.uf}`}
                </div>
                <div style={{ fontSize: 11, opacity: 0.85 }}>
                  {`${state.total.toLocaleString('pt-BR')} ${label}`}
                </div>
                {state.cities > 1 && (
                  <div
                    style={{
                      fontSize: 10,
                      opacity: 0.7,
                      marginTop: 2,
                    }}
                  >
                    {`${state.cities} cidades`}
                  </div>
                )}
              </div>
            </Tooltip>
          </CircleMarker>
        ))}
      </MapContainer>
    </MapWrap>
  );
};

BrazilMap.defaultProps = {
  itemLabel: undefined,
};

export default BrazilMap;
